/**
 * Getting a note out of the editor and into somewhere else.
 *
 * Three destinations, three shapes, and they are genuinely different:
 *
 *   the database   ProseMirror JSON, so structure survives a round trip
 *   Docs / Gmail   HTML on the clipboard, because those paste rich text
 *   Slack          Markdown as PLAIN text, because Slack pastes plain text
 *                  and renders its own *bold* — handing it HTML gets you
 *                  literal angle brackets in a channel
 *
 * The Slack case is why this file exists rather than a call to editor.getHTML.
 * Copying rich text into Slack has been broken everywhere forever, and the fix
 * is not a better HTML string, it is a different format entirely.
 */

/** A ProseMirror node, loosely. Enough to walk without importing the editor. */
export interface DocNode {
  type?: string;
  text?: string;
  /**
   * Attributes are typed loosely because a document holds several node kinds,
   * but `label` is narrowed: it is the one attribute this file stringifies,
   * and `unknown` there produces "[object Object]" in a Slack message rather
   * than a type error.
   */
  attrs?: { label?: string; checked?: boolean; [key: string]: unknown };
  marks?: { type: string; attrs?: Record<string, unknown> }[];
  content?: DocNode[];
}

function hasMark(node: DocNode, name: string): boolean {
  return (node.marks ?? []).some((mark) => mark.type === name);
}

/** Escape the characters Slack would otherwise read as formatting. */
function escapeSlack(text: string): string {
  // Slack's own escaping rules: these three, and only inside text.
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Inline content as Slack-flavoured markup.
 *
 * Slack is NOT CommonMark. Bold is *one* asterisk, italic is _underscore_,
 * strike is ~tilde~. Emitting **double** asterisks — which every generic
 * markdown serializer does — shows up in the channel as literal asterisks
 * around the word.
 */
function inlineToSlack(nodes: DocNode[]): string {
  return nodes
    .map((node) => {
      if (node.type === 'mention') {
        // A mention is a reference to something inside the hub. In Slack it
        // can only be its label — the link would be a hub URL nobody outside
        // the team can open.
        return `*${node.attrs?.label ?? ''}*`;
      }
      if (node.type === 'hardBreak') {
        return '\n';
      }
      let text = escapeSlack(node.text ?? '');
      if (text === '') {
        return '';
      }
      // Innermost first, so nested marks nest rather than interleave.
      if (hasMark(node, 'code')) text = `\`${text}\``;
      if (hasMark(node, 'bold')) text = `*${text}*`;
      if (hasMark(node, 'italic')) text = `_${text}_`;
      if (hasMark(node, 'strike')) text = `~${text}~`;
      // Slack has no underline. Left as plain text rather than faked with
      // something that would read as a different meaning.
      return text;
    })
    .join('');
}

/**
 * The whole document as text Slack will render.
 *
 * Headings become bold lines: Slack has no headings, and a `#` prefix shows
 * up as a literal hash.
 */
export function toSlackMarkdown(doc: DocNode): string {
  const lines: string[] = [];

  function walk(node: DocNode, depth: number, ordinal?: number): void {
    const children = node.content ?? [];
    switch (node.type) {
      case 'doc':
        children.forEach((child) => walk(child, depth));
        return;
      case 'paragraph': {
        lines.push(inlineToSlack(children));
        return;
      }
      case 'heading': {
        const text = inlineToSlack(children);
        lines.push(text === '' ? '' : `*${text}*`);
        return;
      }
      case 'bulletList': {
        children.forEach((child) => walk(child, depth + 1));
        return;
      }
      case 'orderedList': {
        children.forEach((child, index) => walk(child, depth + 1, index + 1));
        return;
      }
      case 'listItem': {
        const indent = '    '.repeat(Math.max(0, depth - 1));
        const marker = ordinal === undefined ? '•' : `${String(ordinal)}.`;
        const [first, ...rest] = children;
        lines.push(`${indent}${marker} ${inlineToSlack(first?.content ?? [])}`);
        rest.forEach((child) => walk(child, depth));
        return;
      }
      case 'taskList': {
        children.forEach((child) => walk(child, depth + 1));
        return;
      }
      case 'taskItem': {
        const indent = '    '.repeat(Math.max(0, depth - 1));
        // Slack renders no checkbox widget, so the box is drawn in text —
        // which is also what makes a copied checklist still read as one.
        const box = node.attrs?.checked === true ? '☑' : '☐';
        const [first, ...rest] = children;
        lines.push(`${indent}${box} ${inlineToSlack(first?.content ?? [])}`);
        rest.forEach((child) => walk(child, depth));
        return;
      }
      case 'codeBlock': {
        lines.push('```');
        lines.push(children.map((child) => child.text ?? '').join(''));
        lines.push('```');
        return;
      }
      case 'blockquote': {
        const before = lines.length;
        children.forEach((child) => walk(child, depth));
        for (let i = before; i < lines.length; i += 1) {
          lines[i] = `> ${lines[i] ?? ''}`;
        }
        return;
      }
      case 'horizontalRule':
        lines.push('———');
        return;
      default:
        children.forEach((child) => walk(child, depth));
    }
  }

  walk(doc, 0);
  // Trailing blank paragraphs are an artefact of the editor always keeping one
  // empty block at the end, not something anybody typed.
  while (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop();
  }
  return lines.join('\n');
}

/**
 * The note's title, taken from its first line.
 *
 * Apple Notes' rule, and the reason there is no title field: you open a note
 * and start writing, and the first thing you write is what the note is called.
 * A separate title box is a box most people leave empty.
 */
export function titleFromDoc(doc: DocNode, fallback = 'New note'): string {
  const blocks = doc.content ?? [];
  for (const block of blocks) {
    const text = plainText(block).trim();
    if (text !== '') {
      // One line only. A first paragraph that runs long is still one title.
      const firstLine = text.split('\n')[0] ?? '';
      return firstLine.length <= 80 ? firstLine : `${firstLine.slice(0, 80).trimEnd()}…`;
    }
  }
  return fallback;
}

/** Every character a human would see, with no markup. */
export function plainText(node: DocNode): string {
  if (node.type === 'mention') {
    return node.attrs?.label ?? '';
  }
  // Shift+Enter. It carries no text of its own, so without this a paragraph
  // broken across two visual lines reads back as one run with the words
  // jammed together — and the title, which is the first LINE, becomes both.
  if (node.type === 'hardBreak') {
    return '\n';
  }
  if (typeof node.text === 'string') {
    return node.text;
  }
  const children = node.content ?? [];
  const parts = children.map((child) => plainText(child));
  // Blocks are separated by a newline; inline runs are not.
  const isBlock = ['doc', 'paragraph', 'heading', 'listItem', 'taskItem', 'blockquote'].includes(
    node.type ?? '',
  );
  return isBlock ? parts.join('') + (node.type === 'doc' ? '' : '\n') : parts.join('');
}

export interface WordCount {
  words: number;
  characters: number;
}

/**
 * Words and characters, as a person would count them.
 *
 * Any run of whitespace separates words, so a double space is not two words
 * and a newline is not a word boundary that gets missed.
 */
export function countWords(doc: DocNode): WordCount {
  const text = plainText(doc).replace(/\s+/g, ' ').trim();
  return {
    words: text === '' ? 0 : text.split(' ').length,
    characters: text.length,
  };
}

/**
 * HTML fit to leave the application.
 *
 * The editor's own HTML carries ProseMirror's bookkeeping — data-* attributes,
 * the class names the toolbar styles itself with, and the contenteditable
 * wrappers. Google Docs will happily paste all of it and then show a document
 * with our class names embedded. This strips it back to tags with meaning.
 */
export function cleanHtml(html: string): string {
  return (
    html
      // ProseMirror and Tiptap bookkeeping.
      .replace(/\sdata-[a-z-]+="[^"]*"/g, '')
      .replace(/\sclass="[^"]*"/g, '')
      .replace(/\scontenteditable="[^"]*"/g, '')
      // Empty trailing paragraph the editor always keeps.
      .replace(/<p><\/p>\s*$/, '')
      .replace(/<p><br\s*\/?><\/p>\s*$/, '')
      .trim()
  );
}

// ── The database column ────────────────────────────────────────────────────

/**
 * Read user_notes.content, whatever era it was written in.
 *
 * The column is text and now holds ProseMirror JSON. Notes written before the
 * editor existed hold plain text, and there is no migration for them on
 * purpose: a migration that reformats somebody's writing is a migration that
 * can corrupt it, and this conversion is exact and reversible. An unparseable
 * value is treated as plain text rather than thrown away.
 */
export function parseContent(raw: string): DocNode {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { type: 'doc', content: [{ type: 'paragraph' }] };
  }
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as DocNode;
      // A JSON value that is not a document is not something the editor can
      // load; falling through treats it as the text it literally is.
      if (parsed.type === 'doc' && Array.isArray(parsed.content)) {
        return parsed;
      }
    } catch {
      // Fall through to the plain-text path below.
    }
  }
  return {
    type: 'doc',
    content: trimmed.split(/\n/).map((line) => ({
      type: 'paragraph',
      // An empty line is a paragraph with no content, not a paragraph holding
      // an empty text node — ProseMirror rejects the latter outright.
      ...(line === '' ? {} : { content: [{ type: 'text', text: line }] }),
    })),
  };
}

export function stringifyContent(doc: DocNode): string {
  return JSON.stringify(doc);
}
